// "Copy these people on candidate email" — a list of addresses, not a textbox.
//
// ─── WHY CHIPS AND NOT A COMMA-SEPARATED TEXT FIELD ─────────────────────────
//
// The column stores a comma-separated string, so a plain TextField would be one
// line of code and would look like it worked. It fails in the way this whole
// change exists to fix: an address with a typo, or a trailing comma leaving an
// empty entry, is silently dropped at send time. On screen the field still
// shows what was typed, so "copied" and "reached nobody" look identical.
//
// Committing each address to a chip forces the validation to happen while the
// person who typed it is still looking at it. A malformed address cannot become
// a chip, so what is on screen is exactly what will be copied.
//
// ─── THE UNCOMMITTED-TEXT TRAP ──────────────────────────────────────────────
//
// The obvious bug in a chip input is text left in the box when the user presses
// Save: they typed an address, did not press Enter, and it vanishes. `onBlur`
// commits, and the parent reads the value through `onChange` only — there is no
// second source of truth to fall out of step. It is still worth knowing about,
// because the fix is easy to delete by accident.

import { useState } from "react";
import { Box, Typography, TextField, Chip, Alert } from "@mui/material";

const TEAL = {
  50: "#e6f7f5",
  100: "#ccefeb",
  300: "#66cfc3",
  500: "#0c8a9b",
  600: "#0a7a8a",
  700: "#086a78",
};

const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

// The same expression the server applies (utils/hiringNotifyRecipients.js) and
// the same one the mailer applies to the candidate's own address. Deliberately
// not stricter: an address this accepts and the server rejects would be a
// rejection the user cannot act on, because the field showed it as valid.
const ADDRESS_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * @param value     comma-separated string as stored, or "" / null
 * @param onChange  called with the new comma-separated string ("" when empty)
 */
const EmailListField = ({
  value,
  onChange,
  label = "Copy these people (CC)",
  helperText,
  placeholder = "name@company.com, then Enter",
  disabled = false,
}) => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const addresses = String(value || "")
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);

  const commit = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return true;

    // Accepts a pasted list in one go. Somebody copying three addresses out of
    // an email client pastes them separated by commas, semicolons or newlines,
    // and rejecting that as "one invalid address" would be needlessly rude.
    const parts = text
      .split(/[,;\s]+/)
      .map((a) => a.trim())
      .filter(Boolean);

    const good = [];
    const bad = [];
    for (const part of parts) {
      if (!ADDRESS_RE.test(part)) {
        bad.push(part);
        continue;
      }
      // Case-insensitive, because a mail server does not care and a duplicate
      // in the header is just noise in the candidate's To line.
      const already = [...addresses, ...good].some(
        (a) => a.toLowerCase() === part.toLowerCase(),
      );
      if (!already) good.push(part);
    }

    if (good.length) onChange([...addresses, ...good].join(", "));

    if (bad.length) {
      setError(
        bad.length === 1
          ? `"${bad[0]}" is not an email address.`
          : `Not email addresses: ${bad.join(", ")}.`,
      );
      // The bad entries stay in the box so they can be corrected rather than
      // retyped. Everything valid has already been committed above.
      setDraft(bad.join(", "));
      return false;
    }

    setError("");
    setDraft("");
    return true;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
      return;
    }
    // Backspace on an empty box removes the last chip — the behaviour every
    // other chip input has, and its absence reads as the field being stuck.
    if (e.key === "Backspace" && !draft && addresses.length) {
      onChange(addresses.slice(0, -1).join(", "));
    }
  };

  const remove = (address) => {
    onChange(addresses.filter((a) => a !== address).join(", "));
  };

  return (
    <Box>
      <Typography
        component="label"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
          fontFamily: fontFam,
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>

      {addresses.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, mb: 0.8 }}>
          {addresses.map((address) => (
            <Chip
              key={address}
              label={address}
              size="small"
              onDelete={disabled ? undefined : () => remove(address)}
              sx={{
                fontFamily: fontFam,
                fontSize: 11.5,
                fontWeight: 600,
                bgcolor: TEAL[50],
                color: TEAL[700],
                border: `1px solid ${TEAL[100]}`,
                "& .MuiChip-deleteIcon": {
                  color: TEAL[600],
                  "&:hover": { color: TEAL[700] },
                },
              }}
            />
          ))}
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError("");
        }}
        onKeyDown={handleKeyDown}
        // Commits what is in the box when focus leaves it. Without this, an
        // address typed and not confirmed with Enter is silently discarded by
        // Save — see the header.
        onBlur={() => commit(draft)}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontSize: 13,
            backgroundColor: "#fff",
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: TEAL[300] },
            "&.Mui-focused fieldset": { borderColor: TEAL[500], borderWidth: 1.5 },
          },
          "& .MuiInputBase-input": {
            fontFamily: fontFam,
            fontSize: 13,
            color: "#1e293b",
          },
        }}
      />

      {helperText && !error && (
        <Typography
          sx={{ fontFamily: fontFam, fontSize: 11, color: "#94a3b8", mt: 0.6 }}
        >
          {helperText}
        </Typography>
      )}

      {error && (
        <Alert
          severity="warning"
          sx={{ mt: 1, borderRadius: "8px", fontSize: 12, fontFamily: fontFam }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default EmailListField;
